import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Bell, Check, Menu, Search, X } from 'lucide-react';
import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';
import {
  hasRegisteredNotificationToken,
  requestBrowserNotifications
} from '../../core/notifications/browserNotifications';
import { ROLE_LABELS } from '../../core/permissions/roles';
import { navigation } from './navigation';

export default function AppShell() {
  const { user, profile, can, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [notificationRegistered, setNotificationRegistered] = useState(false);
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const rawRoles = profile?.adminRoles ?? profile?.adminRole ?? profile?.role ?? [];
  const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles].filter(Boolean);
  const items = navigation.filter((item) => item.permission ? can(item.permission) : item.any?.some(can));

  useEffect(() => {
    let active = true;
    hasRegisteredNotificationToken({ user, db }).then((registered) => {
      if (active) setNotificationRegistered(registered);
    });
    return () => { active = false; };
  }, [user]);

  async function enableNotifications() {
    if (notificationBusy || !user) return;
    setNotificationBusy(true);
    setNotificationMessage('Activation…');
    const result = await requestBrowserNotifications({ user, profile, db });
    setNotificationRegistered(result.registered === true);
    setNotificationMessage(result.message);
    setNotificationBusy(false);
  }

  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? 'is-open' : ''}`}>
        <div className="sidebar-brand">
          <img className="brand-logo" src="/logo.png" alt="Le Défi" />
          <div><strong>Le Défi</strong><span>Administration</span></div>
          <button className="icon-button mobile-only" onClick={() => setOpen(false)} aria-label="Fermer le menu"><X size={20}/></button>
        </div>
        <nav className="sidebar-nav">
          {items.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} end={to === '/dashboard'} onClick={() => setOpen(false)}><Icon size={19}/><span>{label}</span></NavLink>)}
        </nav>
        <div className="sidebar-footer"><div className="avatar">{(profile?.fullname || 'A').slice(0,1).toUpperCase()}</div><div className="identity"><strong>{profile?.fullname || 'Administration'}</strong><span>{roles.map((role) => ROLE_LABELS[role] || role).join(' · ')}</span></div><button className="text-button" onClick={logout}>Quitter</button></div>
      </aside>
      {open && <button className="scrim" onClick={() => setOpen(false)} aria-label="Fermer le menu"/>}
      <section className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-only" onClick={() => setOpen(true)} aria-label="Ouvrir le menu"><Menu size={22}/></button>
          <div className="global-search"><Search size={18}/><input placeholder="Rechercher dans Le Défi…"/></div>
          <button
            className={`notification-enable-button ${notificationRegistered ? 'is-enabled' : ''}`}
            type="button"
            onClick={enableNotifications}
            disabled={notificationBusy || notificationRegistered}
            title={notificationMessage || 'Activer les notifications sur cet appareil'}
          >
            {notificationRegistered ? <Check size={17}/> : <Bell size={17}/>} 
            <span>{notificationBusy ? 'Activation…' : notificationRegistered ? 'Notifications activées' : 'Activer les notifications'}</span>
          </button>
        </header>
        {notificationMessage && !notificationRegistered && (
          <div className="notification-system-message">{notificationMessage}</div>
        )}
        <main className="page"><Outlet/></main>
      </section>
    </div>
  );
}
